import React, { ReactNode, useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import css from './index.module.css';

interface Props {
  children: React.ReactElement<ItemProps> | React.ReactElement<ItemProps>[];
}

export default function Accordion({ children }: Props) {
  return <div className={css['accordion']}>{children}</div>;
}

interface ItemProps {
  header: React.ReactElement<HeaderProps>;
  body: React.ReactElement<BodyProps>;
  opened: boolean;
  ignoreChildElementsOnOpen?: boolean;
  style?: React.CSSProperties;
}

const AccordionItemContext = React.createContext({
  isOpened: null,
  toggleItem: null,
});

export function AccordionItem({
  header,
  body,
  opened,
  ignoreChildElementsOnOpen = false,
  style = null,
}: ItemProps) {
  const [isOpened, setIsOpened] = useState(opened);
  const toggleItem = useCallback(
    (e) => {
      //do not open/close accordion if some child element clicked - exception is chevron (svg + path)
      if (
        ignoreChildElementsOnOpen &&
        e.target.tagName.toUpperCase() !== 'SVG' &&
        e.target.tagName.toUpperCase() !== 'PATH'
      ) {
        if (e.currentTarget !== e.target) return;
      }

      setIsOpened((state) => !state);
    },
    [setIsOpened, ignoreChildElementsOnOpen]
  );

  return (
    <AccordionItemContext.Provider
      value={{ isOpened: isOpened, toggleItem: toggleItem }}
    >
      <div className={css['item']} style={style}>
        {header}
        {body}
      </div>
    </AccordionItemContext.Provider>
  );
}

interface HeaderProps {
  children: ReactNode;
  chevronInRightSide?: boolean;
}

export function AccordionItemHeader({
  children,
  chevronInRightSide = false,
}: HeaderProps) {
  return (
    <AccordionItemContext.Consumer>
      {(AccordionItemContext) => (
        <div
          className={
            css.header + ' ' + (chevronInRightSide ? css.right : css.left)
          }
          onClick={(event) => AccordionItemContext.toggleItem(event)}
        >
          {chevronInRightSide ? (
            <>
              {children}
              {AccordionItemContext.isOpened ? (
                <FontAwesomeIcon icon={faChevronDown} />
              ) : (
                <FontAwesomeIcon icon={faChevronRight} />
              )}
            </>
          ) : (
            <>
              {AccordionItemContext.isOpened ? (
                <FontAwesomeIcon icon={faChevronDown} />
              ) : (
                <FontAwesomeIcon icon={faChevronRight} />
              )}
              {children}
            </>
          )}
        </div>
      )}
    </AccordionItemContext.Consumer>
  );
}

interface BodyProps {
  children: ReactNode;
}

export function AccordionItemBody({ children }: BodyProps) {
  return (
    <AccordionItemContext.Consumer>
      {(AccordionItemContext) => (
        <div
          className={`${css['collapse']} ${
            AccordionItemContext.isOpened ? css['show'] : ''
          }`}
        >
          <div className={css['body']}>{children}</div>
        </div>
      )}
    </AccordionItemContext.Consumer>
  );
}

export function PlainAccordionItemHeader({ children }: HeaderProps) {
  return (
    <AccordionItemContext.Consumer>
      {(AccordionItemContext) => (
        <div onClick={AccordionItemContext.toggleItem}>{children}</div>
      )}
    </AccordionItemContext.Consumer>
  );
}

export function PlainAccordionItemBody({ children }: BodyProps) {
  return (
    <AccordionItemContext.Consumer>
      {(AccordionItemContext) => (
        <div
          className={`${css['collapse']} ${css['plain']} ${
            AccordionItemContext.isOpened ? css['show'] : ''
          }`}
        >
          {children}
        </div>
      )}
    </AccordionItemContext.Consumer>
  );
}
